import React, { useEffect, useState, useRef, Component } from 'react';
import { MatrixBackground } from '../components/MatrixBackground';
import { SalesNotification } from '../components/SalesNotification';
import { LanguageProvider, useLanguage } from '../context/LanguageContext';
import { ClassifiedReveal } from '../components/ClassifiedReveal';
// Interrogation Steps Data
const INTERROGATION_STEPS = [{
  text: '¿Alguna vez has sentido que este mundo no es real?',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/5t2roEBmazR5LNXvxDHPF3/9x16_paso1.png',
  options: [{
    label: 'SÍ',
    action: 'next'
  }]
}, {
  text: '¿Sientes que naciste para algo más... pero no recuerdas qué?',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/9FdphPPSpxQHnkp1k1yeTf/9x16_paso2.png',
  options: [{
    label: 'SÍ',
    action: 'next'
  }]
}, {
  text: '¿Los mismos patrones se repiten en tu vida — diferentes caras, el mismo dolor?',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/1AF8oTddPAnGWFmY6Ddvb5/9x16_paso3.png',
  options: [{
    label: 'SÍ',
    action: 'next'
  }]
}, {
  text: '¿Estás cansado de vivir dormido dentro del sistema?',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/aNNqHqdGWcCQ8Rk8kPEPvg/9x16_paso4.png',
  options: [{
    label: 'SÍ',
    action: 'next'
  }]
}, {
  text: 'Lo que estás a punto de ver es información clasificada.',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/5udJtRM9Vak35B7MEKiF5U/9x16_paso5.png',
  options: [{
    label: 'ENTIENDO',
    action: 'next'
  }]
}, {
  text: 'No hay vuelta atrás. Tú decides.',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/w8JN2UgvFc3dvjaMJQGAZD/9x16_paso6.png',
  options: [{
    label: 'PASTILLA AZUL (SALIR)',
    action: 'blue_pill',
    color: 'blue'
  }, {
    label: 'PASTILLA ROJA (DESPERTAR)',
    action: 'next',
    color: 'red'
  }]
}, {
  text: 'Acceso concedido. Bienvenido al juego real.',
  mobileImage: 'https://cdn.magicpatterns.com/uploads/1tsue3euWhKZDwMm6BsUUm/9x16_paso7.png',
  options: [{
    label: 'ENTRAR AL SISTEMA',
    action: 'finish'
  }]
}];
// Inner component to handle language forcing and content
function LP1Content() {
  const {
    setLanguage
  } = useLanguage();
  // 0: Off, 1: Power On, 2: Flash, 3: Boot Text, 4: Interrogation, 5: Complete
  const [bootPhase, setBootPhase] = useState(0);
  const [bootText, setBootText] = useState<string[]>([]);
  // Interrogation State
  const [interrogationStep, setInterrogationStep] = useState(0);
  const [displayedQuestion, setDisplayedQuestion] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);
  const [fadingImage, setFadingImage] = useState<string | null>(null);
  const [audioStarted, setAudioStarted] = useState(false);
  const [showLastChance, setShowLastChance] = useState(false);
  const ambientAudioRef = useRef<HTMLAudioElement>(null);
  // Ambient audio URL (binaural meditation frequency)
  const AMBIENT_AUDIO_URL = 'https://share.deal.ai/files/6902f93c500a402c689c3020/d71e663b-2ff1-4919-8ed0-156f7315d3ca';
  const startAmbientAudio = () => {
    if (audioStarted) return;
    const audio = ambientAudioRef.current;
    if (audio) {
      audio.volume = 0.25;
      audio.loop = false;
      audio.play().catch(() => {});
      setAudioStarted(true);
    }
  };
  useEffect(() => {
    setLanguage('es');
    // Boot Sequence
    const sequence = async () => {
      // Phase 0: Black (Start)
      await new Promise(r => setTimeout(r, 200));
      // Phase 1: Power On (Line expand)
      setBootPhase(1);
      await new Promise(r => setTimeout(r, 600));
      // Phase 2: White Flash
      setBootPhase(2);
      await new Promise(r => setTimeout(r, 200));
      // Phase 3: Boot Text
      setBootPhase(3);
      const lines = ['> SYSTEM BIOS V1.04', '> CHECKING MEMORY... 640K OK', '> LOADING MATRIX KERNEL...', '> ESTABLISHING SECURE CONNECTION...', '> ENCRYPTING DATA STREAMS...', '> ACCESS GRANTED.', '> WAKE UP.'];
      for (const line of lines) {
        setBootText(prev => [...prev, line]);
        await new Promise(r => setTimeout(r, 300 + Math.random() * 200));
      }
      await new Promise(r => setTimeout(r, 800));
      // Phase 4: Start Interrogation
      setBootPhase(4);
    };
    sequence();
  }, [setLanguage]);
  // Show "last chance" button after 2s on blue pill screen
  useEffect(() => {
    if (bootPhase === 6) {
      setShowLastChance(false);
      const timer = setTimeout(() => setShowLastChance(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [bootPhase]);
  // Pause ambient audio when reaching the sales page (bootPhase 5)
  useEffect(() => {
    if (bootPhase === 5) {
      const audio = ambientAudioRef.current;
      if (audio) {
        audio.pause();
      }
    }
  }, [bootPhase]);
  // Typewriter Effect for Interrogation
  useEffect(() => {
    if (bootPhase !== 4) return;
    const currentStepData = INTERROGATION_STEPS[interrogationStep];
    if (!currentStepData) return;
    setDisplayedQuestion('');
    setIsTyping(true);
    setShowOptions(false);
    let charIndex = 0;
    const text = currentStepData.text;
    const typeInterval = setInterval(() => {
      if (charIndex < text.length) {
        setDisplayedQuestion(prev => prev + text.charAt(charIndex));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        setTimeout(() => setShowOptions(true), 400);
      }
    }, 40);
    return () => clearInterval(typeInterval);
  }, [bootPhase, interrogationStep]);
  const handleOptionClick = async (action: string) => {
    // Start ambient audio on first interaction
    startAmbientAudio();
    // Fire AddToCart pixel when user clicks Red Pill (step 6, index 5)
    if (action === 'next' && interrogationStep === 5) {
      console.log('[Meta Pixel] Firing AddToCart event');
      const f = window as any;
      if (f.fbq) {
        f.fbq('track', 'AddToCart');
      }
      // Fallback: fire via image pixel in case fbq isn't loaded yet
      const img = new Image();
      img.src = 'https://www.facebook.com/tr?id=1398555731600742&ev=AddToCart&noscript=1';
      img.width = 1;
      img.height = 1;
      img.style.display = 'none';
      document.body.appendChild(img);
    }
    if (action === 'blue_pill') {
      // Stop ambient audio
      const audio = ambientAudioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      // Show blue pill screen with glitch
      setGlitchActive(true);
      await new Promise(r => setTimeout(r, 500));
      setGlitchActive(false);
      setBootPhase(6);
      return;
    }
    // Get current step's mobile image before advancing
    const currentImage = INTERROGATION_STEPS[interrogationStep]?.mobileImage || '';
    // Brief glitch (shake + static + image flash) for 300ms
    setGlitchActive(true);
    await new Promise(r => setTimeout(r, 300));
    setGlitchActive(false);
    // Set the fading background image (will fade out over 2s)
    if (currentImage) {
      setFadingImage(currentImage);
      setTimeout(() => setFadingImage(null), 2000);
    }
    // Advance to next step
    if (action === 'next') {
      setInterrogationStep(prev => prev + 1);
    } else if (action === 'finish') {
      setFadingImage(null);
      setBootPhase(5);
    }
  };
  return <div className="matrix-terminal w-full min-h-screen bg-black text-[#00ff41] font-mono relative overflow-x-hidden" data-id="element-612">
      {/* Ambient Audio - plays throughout the flow */}
      <audio ref={ambientAudioRef} src={AMBIENT_AUDIO_URL} preload="auto" crossOrigin="anonymous" data-id="element-613" />

      {/* Global CSS Overrides for Matrix Theme */}
      <style data-id="element-614">{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

        :root {
          --matrix-green: #00ff41;
          --matrix-dark: #003b00;
          --matrix-dim: #008f11;
          --matrix-black: #000000;
        }

        .matrix-terminal {
          font-family: 'Share Tech Mono', 'Courier New', monospace !important;
        }

        /* Text Color Overrides */
        .matrix-terminal .text-teal-100,
        .matrix-terminal .text-teal-200,
        .matrix-terminal .text-teal-300,
        .matrix-terminal .text-teal-400,
        .matrix-terminal .text-teal-500,
        .matrix-terminal .text-purple-100,
        .matrix-terminal .text-purple-200,
        .matrix-terminal .text-purple-300,
        .matrix-terminal .text-purple-400,
        .matrix-terminal .text-yellow-400, 
        .matrix-terminal .text-yellow-300 {
          color: var(--matrix-green) !important;
          text-shadow: 0 0 5px rgba(0, 255, 65, 0.5);
        }

        .matrix-terminal .text-gray-200,
        .matrix-terminal .text-gray-300,
        .matrix-terminal .text-gray-400 {
          color: #b0ffb0 !important;
        }

        /* Keep Red for "Red Pill" effect but make it glow */
        .matrix-terminal .text-red-300,
        .matrix-terminal .text-red-400 {
          color: #ff3333 !important;
          text-shadow: 0 0 8px rgba(255, 0, 0, 0.6);
        }

        /* Background Overrides - Kill the gradients */
        .matrix-terminal .bg-gradient-to-b,
        .matrix-terminal .bg-gradient-to-br,
        .matrix-terminal .bg-gradient-to-r {
          background-image: none !important;
          background-color: rgba(0, 10, 0, 0.9) !important;
        }

        .matrix-terminal section {
          background-color: transparent !important;
          border-bottom: 1px solid rgba(0, 255, 65, 0.1);
        }

        /* Card/Container Styles - Terminal Windows */
        .matrix-terminal .rounded-xl,
        .matrix-terminal .rounded-2xl,
        .matrix-terminal .rounded-lg {
          border-radius: 2px !important;
          border: 1px solid var(--matrix-dim) !important;
          background-color: rgba(0, 20, 0, 0.8) !important;
          box-shadow: 0 0 10px rgba(0, 255, 65, 0.1) !important;
        }

        /* Buttons - "Red Pill" vs "Green Pill" */
        /* Red buttons (CTAs) */
        .matrix-terminal button[class*="from-red-600"],
        .matrix-terminal a[class*="from-red-600"] {
          background: #000 !important;
          border: 1px solid #ff0000 !important;
          color: #ff0000 !important;
          box-shadow: 0 0 10px rgba(255, 0, 0, 0.3) !important;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        
        .matrix-terminal button[class*="from-red-600"]:hover,
        .matrix-terminal a[class*="from-red-600"]:hover {
          background: rgba(255, 0, 0, 0.1) !important;
          box-shadow: 0 0 20px rgba(255, 0, 0, 0.6) !important;
        }

        /* Other buttons (formerly yellow/teal/purple) -> Green Terminal Buttons */
        .matrix-terminal button:not([class*="from-red-600"]),
        .matrix-terminal a:not([class*="from-red-600"]) {
          background: #000 !important;
          border: 1px solid var(--matrix-green) !important;
          color: var(--matrix-green) !important;
          box-shadow: 0 0 5px rgba(0, 255, 65, 0.3) !important;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .matrix-terminal button:not([class*="from-red-600"]):hover,
        .matrix-terminal a:not([class*="from-red-600"]):hover {
          background: rgba(0, 255, 65, 0.1) !important;
          box-shadow: 0 0 15px rgba(0, 255, 65, 0.5) !important;
        }

        /* Icons */
        .matrix-terminal svg {
          color: var(--matrix-green) !important;
          filter: drop-shadow(0 0 2px var(--matrix-green));
        }
        
        .matrix-terminal svg.text-red-400 {
          color: #ff3333 !important;
          filter: drop-shadow(0 0 2px #ff0000);
        }

        /* Effects */
        .scanlines {
          background: linear-gradient(
            to bottom,
            rgba(255,255,255,0),
            rgba(255,255,255,0) 50%,
            rgba(0,0,0,0.2) 50%,
            rgba(0,0,0,0.2)
          );
          background-size: 100% 4px;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 50;
        }

        .vignette {
          background: radial-gradient(
            circle,
            rgba(0,0,0,0) 60%,
            rgba(0,0,0,0.6) 100%
          );
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 51;
        }

        .crt-flicker {
          animation: flicker 0.15s infinite;
        }

        @keyframes flicker {
          0% { opacity: 0.97; }
          50% { opacity: 1; }
          100% { opacity: 0.98; }
        }

        /* CRT Boot Animations */
        @keyframes turn-on {
          0% {
            transform: scale(1, 0.8) translate3d(0, 0, 0);
            -webkit-filter: brightness(30);
            filter: brightness(30);
            opacity: 1;
          }
          3.5% {
            transform: scale(1, 0.8) translate3d(0, 100%, 0);
          }
          3.6% {
            transform: scale(1, 0.8) translate3d(0, -100%, 0);
            opacity: 1;
          }
          9% {
            transform: scale(1.3, 0.6) translate3d(0, 100%, 0);
            -webkit-filter: brightness(30);
            filter: brightness(30);
            opacity: 0;
          }
          11% {
            transform: scale(1, 1) translate3d(0, 0, 0);
            -webkit-filter: contrast(0) brightness(0);
            filter: contrast(0) brightness(0);
            opacity: 0;
          }
          100% {
            transform: scale(1, 1) translate3d(0, 0, 0);
            -webkit-filter: contrast(1) brightness(1.2) saturate(1.3);
            filter: contrast(1) brightness(1.2) saturate(1.3);
            opacity: 1;
          }
        }

        @keyframes crt-power-on-line {
          0% {
            width: 0%;
            height: 2px;
            opacity: 0;
            background: #fff;
          }
          10% {
            width: 100%;
            height: 2px;
            opacity: 1;
            background: #fff;
          }
          40% {
            width: 100%;
            height: 2px;
            opacity: 1;
            background: #fff;
          }
          100% {
            width: 100%;
            height: 100vh;
            opacity: 1;
            background: #e8e6d0;
          }
        }

        .crt-power-line {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 100;
          animation: crt-power-on-line 0.6s ease-out forwards;
        }

        .crt-flash {
          position: fixed;
          inset: 0;
          background: #e8e6d0;
          z-index: 100;
          opacity: 1;
          transition: opacity 0.2s ease-out;
        }

        .boot-screen {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          z-index: 90;
          padding: 2rem;
          font-family: 'Share Tech Mono', monospace;
          color: #00ff41;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
        }

        .interrogation-screen {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          z-index: 90;
          padding: 2rem;
          font-family: 'Share Tech Mono', monospace;
          color: #00ff41;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        .cursor-blink {
          display: inline-block;
          width: 10px;
          height: 1.2em;
          background: #00ff41;
          animation: blink 1s step-end infinite;
          vertical-align: middle;
          margin-left: 4px;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .glitch-active {
          animation: screen-shake 0.15s infinite;
        }

        @keyframes screen-shake {
          0% { transform: translate(0, 0) }
          10% { transform: translate(-4px, 3px) }
          20% { transform: translate(5px, -2px) }
          30% { transform: translate(-3px, -4px) }
          40% { transform: translate(4px, 5px) }
          50% { transform: translate(-5px, 2px) }
          60% { transform: translate(3px, -3px) }
          70% { transform: translate(-2px, 4px) }
          80% { transform: translate(5px, -5px) }
          90% { transform: translate(-4px, 3px) }
          100% { transform: translate(0, 0) }
        }

        /* Static noise overlay */
        .static-noise {
          position: fixed;
          inset: 0;
          z-index: 999;
          opacity: 0.15;
          pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
          background-size: 128px 128px;
          animation: static-scroll 0.1s steps(3) infinite;
        }

        @keyframes static-scroll {
          0% { background-position: 0 0; }
          33% { background-position: -50px -30px; }
          66% { background-position: 30px -60px; }
          100% { background-position: -20px 40px; }
        }

        /* Morpheus flash image */
        .morpheus-flash {
          position: fixed;
          inset: 0;
          z-index: 998;
          background-color: #000;
        }

        .morpheus-flash img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Fading background image behind interrogation text */
        .fading-bg-image {
          position: absolute;
          inset: 0;
          z-index: 91;
          pointer-events: none;
          animation: fade-out-bg 2s ease-out forwards;
        }

        .fading-bg-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        @keyframes fade-out-bg {
          0% { opacity: 0.9; }
          30% { opacity: 0.7; }
          60% { opacity: 0.4; }
          100% { opacity: 0; }
        }

        /* Hide language selector if it pops up */
        .matrix-terminal .fixed.inset-0.bg-black\\/90 {
          display: none !important;
        }
        
        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          background: #000;
        }
        ::-webkit-scrollbar-thumb {
          background: #003b00;
        }
        ::-webkit-scrollbar-track {
          background: #000;
        }

        /* Prevent horizontal overflow */
        html, body {
          overflow-x: hidden !important;
          max-width: 100vw !important;
        }

        /* Force Matrix canvas to be absolute inside overlay */
        .overlay-matrix-wrapper canvas {
          position: absolute !important;
          z-index: auto !important;
          inset: 0 !important;
        }

        /* Blue Pill Glitch Loop */
        .blue-pill-glitch {
          animation: bp-glitch-loop 4s ease-in-out infinite;
        }

        @keyframes bp-glitch-loop {
          0%, 100% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
          5% { transform: translate(-3px, 1px) skewX(-0.5deg); filter: brightness(1.3) saturate(0.5); }
          10% { transform: translate(2px, -1px) skewX(0.3deg); filter: brightness(0.8) saturate(0.9); }
          15% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
          40% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
          42% { transform: translate(5px, -2px) skewX(1deg); filter: brightness(1.5) saturate(0.3); }
          43% { transform: translate(-4px, 3px) skewX(-1.5deg); filter: brightness(0.6) saturate(1.2); }
          44% { transform: translate(3px, 0px) skewX(0.5deg); filter: brightness(1.8) saturate(0.2); }
          46% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
          70% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
          71% { transform: translate(-2px, 1px) skewX(-0.3deg); filter: brightness(1.2) saturate(0.6); }
          73% { transform: translate(0, 0) skewX(0deg); filter: brightness(1) saturate(0.7); }
        }

        .blue-pill-rgb-shift {
          animation: bp-rgb-shift 3s ease-in-out infinite;
        }

        @keyframes bp-rgb-shift {
          0%, 100% { transform: translate(0, 0); opacity: 0; }
          40% { transform: translate(0, 0); opacity: 0; }
          42% { transform: translate(4px, -2px); opacity: 0.4; }
          44% { transform: translate(-3px, 1px); opacity: 0.3; }
          46% { transform: translate(0, 0); opacity: 0; }
          70% { transform: translate(0, 0); opacity: 0; }
          71% { transform: translate(2px, -1px); opacity: 0.2; }
          73% { transform: translate(0, 0); opacity: 0; }
        }

        .blue-pill-scanbar {
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(59, 130, 246, 0.03) 45%,
            rgba(59, 130, 246, 0.08) 50%,
            rgba(59, 130, 246, 0.03) 55%,
            transparent 100%
          );
          background-size: 100% 200%;
          animation: bp-scanbar 3s linear infinite;
        }

        @keyframes bp-scanbar {
          0% { background-position: 0 -100%; }
          100% { background-position: 0 200%; }
        }

        .blue-pill-text-flicker {
          animation: bp-text-flicker 2.5s ease-in-out infinite;
        }

        @keyframes bp-text-flicker {
          0%, 100% { opacity: 0.9; }
          10% { opacity: 0.85; }
          20% { opacity: 0.95; }
          30% { opacity: 0.7; }
          31% { opacity: 1; }
          50% { opacity: 0.9; }
          70% { opacity: 0.95; }
          80% { opacity: 0.6; }
          81% { opacity: 0.95; }
        }
      `}</style>

      {/* Boot Sequence & Interrogation Overlay */}
      {bootPhase < 5 && <div className={`fixed inset-0 z-[100] bg-black ${glitchActive ? 'glitch-active' : ''}`} data-id="element-615">
          {/* Morpheus Flash + Static Noise during glitch */}
          {glitchActive && <>
              <div className="morpheus-flash" data-id="element-616">
                {/* Mobile image - unique per paso (9x16) */}
                {INTERROGATION_STEPS[interrogationStep]?.mobileImage ? <img src={INTERROGATION_STEPS[interrogationStep].mobileImage} alt="" className="block md:hidden" data-id="element-617" /> : <img src="https://cdn.magicpatterns.com/uploads/k1LmjqV4sBawcEXjJx1rpg/9x16.png" alt="" className="block md:hidden" data-id="element-618" />}
                {/* Desktop image (16x9) - same for all */}
                <img src="https://cdn.magicpatterns.com/uploads/coqq465nhxuREKVpmWEnDx/16x9.png" alt="" className="hidden md:block" data-id="element-619" />
              </div>
              <div className="static-noise" data-id="element-620"></div>
            </>}

          {/* Subtle Matrix Rain Behind All Phases */}
          <div className="overlay-matrix-wrapper" style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        opacity: 0.4,
        zIndex: 1
      }} data-id="element-621">
            <MatrixBackground data-id="element-622" />
          </div>

          {/* Phase 1: Power On Line */}
          {bootPhase === 1 && <div className="crt-power-line" data-id="element-623"></div>}

          {/* Phase 2: White Flash */}
          {bootPhase === 2 && <div className="crt-flash" data-id="element-624"></div>}

          {/* Phase 3: Boot Text */}
          {bootPhase === 3 && <div className="boot-screen" data-id="element-625">
              {bootText.map((line, i) => <div key={i} className="mb-2 text-lg md:text-xl" data-id="element-626">
                  {line}
                </div>)}
              <div className="mt-2 text-lg md:text-xl" data-id="element-627">
                <span className="cursor-blink" data-id="element-628"></span>
              </div>
            </div>}

          {/* Phase 4: Interrogation */}
          {bootPhase === 4 && <div className="interrogation-screen" data-id="element-629">
              {/* Persistent background image for step 6 (red pill / blue pill) */}
              {interrogationStep === 5 && <div className="absolute inset-0" style={{
          zIndex: 91,
          pointerEvents: 'none'
        }} data-id="element-630">
                  <img src="https://cdn.magicpatterns.com/uploads/nCy8GZWDumQXb4zKF9SEXB/9x16_last_step.png" alt="" className="w-full h-full object-cover" data-id="element-631" />
                  <div className="absolute inset-0 bg-black/40" data-id="element-632"></div>
                </div>}

              {/* Fading image from previous paso */}
              {fadingImage && <div className="fading-bg-image" key={fadingImage} data-id="element-633">
                  <img src={fadingImage} alt="" className="block md:hidden" data-id="element-634" />
                  <img src="https://cdn.magicpatterns.com/uploads/coqq465nhxuREKVpmWEnDx/16x9.png" alt="" className="hidden md:block" data-id="element-635" />
                </div>}

              <div className="absolute top-4 left-4 text-xs md:text-sm border border-[#00ff41] px-2 py-1" style={{
          zIndex: 92
        }} data-id="element-636">
                PASO {interrogationStep + 1}/{INTERROGATION_STEPS.length}
              </div>

              <div className="max-w-3xl w-full px-4" style={{
          zIndex: 92,
          position: 'relative'
        }} data-id="element-637">
                <h2 className="text-2xl md:text-4xl mb-12 leading-relaxed min-h-[120px]" data-id="element-638">
                  {displayedQuestion}
                  <span className="cursor-blink" data-id="element-639"></span>
                </h2>

                <div className={`flex flex-col md:flex-row gap-4 justify-center items-center transition-opacity duration-500 ${showOptions ? 'opacity-100' : 'opacity-0'}`} data-id="element-640">
                  {INTERROGATION_STEPS[interrogationStep].options.map((option, idx) => <button key={idx} onClick={() => handleOptionClick(option.action)} className={`
                        px-8 py-4 text-xl font-bold border-2 transition-all transform hover:scale-105
                        ${option.color === 'blue' ? 'border-blue-500 text-blue-500 hover:bg-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : option.color === 'red' ? 'border-red-500 text-red-500 hover:bg-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.5)]' : 'border-[#00ff41] text-[#00ff41] hover:bg-[#00ff41]/20 shadow-[0_0_10px_rgba(0,255,65,0.5)]'}
                      `} data-id="element-641">
                        {option.label}
                      </button>)}
                </div>
              </div>
            </div>}
        </div>}

      {/* Blue Pill Screen - Looping Glitch */}
      {bootPhase === 6 && <div className="fixed inset-0 z-[100] bg-black blue-pill-glitch" data-id="element-642">
          {/* Background Image */}
          <div className="absolute inset-0" data-id="element-643">
            <img src="https://cdn.magicpatterns.com/uploads/n4SHUGV5P93ErX2zUPa6Qz/blue_pill.png" alt="" className="w-full h-full object-cover" data-id="element-644" />
            <div className="absolute inset-0 bg-black/20" data-id="element-645"></div>
          </div>

          {/* Glitch color channels */}
          <div className="blue-pill-rgb-shift absolute inset-0 pointer-events-none" style={{
        zIndex: 2
      }} data-id="element-646">
            <img src="https://cdn.magicpatterns.com/uploads/n4SHUGV5P93ErX2zUPa6Qz/blue_pill.png" alt="" className="w-full h-full object-cover" style={{
          mixBlendMode: 'screen',
          opacity: 0.3
        }} data-id="element-647" />
          </div>

          {/* Static noise overlay - persistent */}
          <div className="static-noise" style={{
        zIndex: 3,
        opacity: 0.12
      }} data-id="element-648"></div>

          {/* Scanline bars that sweep */}
          <div className="blue-pill-scanbar absolute inset-0 pointer-events-none" style={{
        zIndex: 4
      }} data-id="element-649"></div>

          {/* Vignette */}
          <div className="absolute inset-0 pointer-events-none" style={{
        zIndex: 5,
        background: 'radial-gradient(circle, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)'
      }} data-id="element-650"></div>

          {/* Text overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-20 md:pb-24 px-6" style={{
        zIndex: 6
      }} data-id="element-651">
            <p className="text-blue-400 text-lg md:text-2xl font-mono tracking-widest text-center blue-pill-text-flicker" style={{
          textShadow: '0 0 10px rgba(59,130,246,0.8), 0 0 30px rgba(59,130,246,0.4)'
        }} data-id="element-652">
              CONEXIÓN PERDIDA
            </p>
            <p className="text-gray-500 text-xs md:text-sm font-mono mt-3 tracking-wider text-center" data-id="element-653">
              REGRESANDO AL SISTEMA...
            </p>

            {/* Last chance button - appears after 2 seconds */}
            <button onClick={() => {
          // Resume audio
          const audio = ambientAudioRef.current;
          if (audio) {
            audio.volume = 0.25;
            audio.play().catch(() => {});
          }
          // Jump to step 7 (index 6) - "Acceso concedido"
          setInterrogationStep(6);
          setBootPhase(4);
          setShowLastChance(false);
        }} className={`mt-8 px-6 py-3 border-2 border-red-500 text-red-500 font-mono text-sm md:text-base tracking-wider transition-all duration-1000 ${showLastChance ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`} style={{
          textShadow: '0 0 8px rgba(239,68,68,0.6)',
          boxShadow: showLastChance ? '0 0 15px rgba(239,68,68,0.3), 0 0 30px rgba(239,68,68,0.15)' : 'none',
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)'
        }} data-id="element-654">
              CAMBIÉ DE OPINIÓN — LLÉVAME AL DESPERTAR
            </button>
          </div>
        </div>}

      {/* Effects Layers */}
      <div className="scanlines" data-id="element-655"></div>
      <div className="vignette" data-id="element-656"></div>

      {/* Matrix Background - Lower Opacity */}
      <div className="fixed inset-0 z-0 opacity-20" data-id="element-657">
        <MatrixBackground data-id="element-658" />
      </div>

      {/* Top Secret Header */}
      <div className="fixed top-0 left-0 w-full bg-black border-b border-[#00ff41] z-40 px-4 py-1 flex justify-between items-center text-xs md:text-sm tracking-widest uppercase" data-id="element-659">
        <span className="text-[#00ff41] animate-pulse" data-id="element-660">● CONEXIÓN SEGURA</span>
        <span className="text-red-500 font-bold border border-red-500 px-2" data-id="element-661">
          ARCHIVO CLASIFICADO: NIVEL 7
        </span>
        <span className="text-[#00ff41] hidden md:inline" data-id="element-662">IP: 192.168.0.1</span>
      </div>

      <div className={`relative z-10 pt-8 crt-flicker transition-opacity duration-1000 ${bootPhase === 5 ? 'opacity-100' : 'opacity-0'}`} data-id="element-663">
        <SalesNotification data-id="element-664" />

        {/* Minimalist Classified Reveal Component */}
        <ClassifiedReveal data-id="element-665" />
      </div>
    </div>;
}
export function LP1Page() {
  return <LanguageProvider data-id="element-666">
      <LP1Content data-id="element-667" />
    </LanguageProvider>;
}